import { useSeoMeta } from "@unhead/react"
import {FileText, Shield, Scale, Mail, Phone, ExternalLink} from 'lucide-react'
import { brandName, siteName } from "@/data/site"

function CGV() {
  useSeoMeta({
    title: `Conditions Générales de Vente · ${siteName}`,
    description: `Conditions Générales de Vente des prestations d'accompagnement périnatal et bien-être de ${brandName} (Keraudran Maïwenn).`,
    ogTitle: `CGV · ${siteName}`,
    ogDescription: `Conditions Générales de Vente des prestations d'accompagnement périnatal et bien-être de ${brandName}.`,
    twitterCard: "summary_large_image",
  })

  return (
    <div className="text-[#2d221b]">
      {/* En-tête */}
      <section className="mx-auto max-w-4xl px-5 pb-8 pt-14 text-center md:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
          <FileText size={14} />
          <span>Cadre contractuel</span>
        </div>
        <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-5xl">
          Conditions Générales de Vente
        </h1>
        <p className="mt-2 text-base font-semibold text-[#8c6b58]">
          Au creux de maman
        </p>
        <p className="mx-auto mt-2 max-w-2xl text-xs uppercase tracking-wider text-[#8c6b58]">
          Dernière mise à jour : septembre 2026
        </p>
      </section>

      {/* Contenu complet des CGV */}
      <section className="mx-auto max-w-4xl px-5 pb-20 md:px-8">
        <div className="rounded-3xl border border-[#dfd0c2] bg-white p-6 shadow-warm-xs md:p-12 space-y-10 text-sm leading-relaxed text-[#5e4b3e]">
          
          {/* Article 1 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 1 – Identité du professionnel
            </h2>
            <div className="mt-4 space-y-3">
              <p>Les présentes Conditions Générales de Vente (CGV) sont proposées par :</p>
              <div className="rounded-2xl border border-[#ebdcd0] bg-[#faf6f0] p-4 text-[#2d221b] space-y-1.5">
                <p className="font-semibold text-base">Keraudran Maïwenn</p>
                <p>Entreprise individuelle exerçant sous le nom commercial <strong>« Au creux de maman »</strong></p>
<p>19 rue Louis Le Moenic — 56240 Inguiniel – France</p>
                <p>SIRET : <strong>883 612 897 00032</strong></p>
                <p>Téléphone : <strong>06 25 89 56 64</strong></p>
                <p>
                  E-mail :{" "}
                  <a href="mailto:aucreuxdemaman@gmail.com" className="text-[#b85c38] font-medium hover:underline">
                    aucreuxdemaman@gmail.com
                  </a>
                </p>
                <p>
                  Site internet :{" "}
                  <a href="https://aucreuxdemaman.lumi.ing/" target="_blank" rel="noreferrer" className="text-[#b85c38] font-medium hover:underline">
                    https://aucreuxdemaman.lumi.ing/
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Article 2 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 2 – Objet
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Les présentes CGV définissent les conditions dans lesquelles Au creux de maman propose des prestations d’accompagnement périnatal et de bien-être destinées aux particuliers.
              </p>
              <p>Les prestations proposées comprennent notamment :</p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>accompagnement doula ;</li>
                <li>accompagnement autour du désir d’enfant ;</li>
                <li>accompagnement pendant la grossesse ;</li>
                <li>accompagnement autour du projet de naissance ;</li>
                <li>accompagnement postnatal ;</li>
                <li>massage bébé ;</li>
                <li>atelier réflexologie émotionnelle ;</li>
                <li>bain enveloppé du bébé ;</li>
                <li>massage fertilité ;</li>
                <li>massage prénatal ;</li>
                <li>massage postnatal ;</li>
                <li>formule Duo (2 massages au choix) ;</li>
                <li>formule Complète (3 massages) ;</li>
                <li>consultation d’allaitement.</li>
              </ul>
              <div className="rounded-xl border border-[#ebdcd0] bg-[#faf6f0] p-4 text-[#8c4828] text-xs font-medium leading-relaxed">
                Les prestations proposées ne constituent pas des actes médicaux, paramédicaux ou thérapeutiques et ne se substituent en aucun cas au suivi assuré par un médecin, une sage-femme, un infirmier ou tout autre professionnel de santé.
              </div>
            </div>
          </div>

          {/* Article 3 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 3 – Public concerné
            </h2>
            <div className="mt-4 space-y-2">
              <p>Les prestations sont destinées exclusivement aux particuliers.</p>
              <p>Le client déclare être juridiquement capable de conclure un contrat.</p>
              <p>Pour les prestations concernant un mineur, le consentement du représentant légal est requis.</p>
            </div>
          </div>

          {/* Article 4 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 4 – Réservation
            </h2>
            <div className="mt-4 space-y-3">
              <p>Les réservations peuvent être effectuées :</p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>par téléphone au <strong>06 25 89 56 64</strong> ;</li>
                <li>par l’intermédiaire de Calendly, accessible depuis le site internet ou directement via le lien de réservation communiqué par Au creux de maman.</li>
              </ul>
              <p>
                La réservation devient ferme après confirmation du rendez-vous et, lorsqu&apos;un paiement est demandé avant la prestation, après réception du règlement selon les modalités convenues.
              </p>
            </div>
          </div>

          {/* Article 5 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 5 – Prestations et tarifs
            </h2>
            <div className="mt-4 space-y-4">
              <p>Les tarifs applicables sont les suivants :</p>
              <div className="overflow-x-auto rounded-2xl border border-[#ebdcd0]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#f7f0e8] text-[#2d221b] border-b border-[#ebdcd0]">
                      <th className="py-3 px-4 font-semibold">Prestation</th>
                      <th className="py-3 px-4 text-right font-semibold">Tarif</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#ebdcd0] bg-white">
                    <tr>
                      <td className="py-2.5 px-4">Atelier réflexologie émotionnelle – Individuel : 50 € / Collectif</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">20 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Massage bébé</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">50 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Bain enveloppé</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">80 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Massage fertilité – 1h15</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">70 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Massage prénatal – 1h15</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">70 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Massage postnatal</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">80 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Formule Duo – 2 massages au choix</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">130 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Formule Complète – 3 massages</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">200 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Consultation allaitement – 1h30</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">50 €</td>
                    </tr>
                    <tr>
                      <td className="py-2.5 px-4">Consultation doula – 1h30</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[#2d221b]">60 €</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>Les prix sont indiqués en euros.</p>
              <p className="font-medium text-[#2d221b]">
                TVA non applicable, article 293 B du Code général des impôts.
              </p>
              <p>
                Les tarifs applicables sont ceux indiqués au client au moment de la réservation.
              </p>
            </div>
          </div>

          {/* Article 6 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 6 – Modalités de paiement
            </h2>
            <div className="mt-4 space-y-3">
              <p>Le règlement peut être effectué :</p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>par chèque ;</li>
                <li>par virement bancaire.</li>
              </ul>
              <p>Le paiement est dû selon les modalités communiquées lors de la réservation.</p>
              <p>Aucun frais supplémentaire ne sera facturé au client sans information préalable.</p>
            </div>
          </div>

          {/* Article 7 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 7 – Annulation et report par le client
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Toute annulation ou demande de report doit être effectuée au minimum <strong>48 heures avant le rendez-vous</strong>.
              </p>
              <p>
                En cas d&apos;annulation effectuée moins de 48 heures avant le rendez-vous, ou d&apos;absence non justifiée, le montant intégral de la prestation réservée pourra être demandé.
              </p>
              <p>
                Lorsqu&apos;un motif légitime ou un cas de force majeure justifie l&apos;annulation tardive, Au creux de maman pourra apprécier la situation et proposer, le cas échéant, un report du rendez-vous.
              </p>
            </div>
          </div>

          {/* Article 8 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 8 – Annulation par Au creux de maman
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                En cas d&apos;impossibilité exceptionnelle d&apos;assurer une prestation, Au creux de maman en informe le client dans les meilleurs délais et propose, lorsque cela est possible, un report du rendez-vous.
              </p>
              <p>
                Si aucun report n&apos;est possible ou accepté, toute somme déjà versée au titre de la prestation non réalisée sera remboursée.
              </p>
            </div>
          </div>

          {/* Article 9 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 9 – Droit de rétractation
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Lorsque le contrat est conclu à distance, notamment par internet ou par téléphone, le consommateur dispose en principe d&apos;un délai légal de <strong>14 jours</strong> pour exercer son droit de rétractation à compter de la conclusion du contrat de prestation de services.
              </p>
              <p>Le client peut exercer ce droit en adressant une demande claire à :</p>
              <div className="rounded-2xl border border-[#ebdcd0] bg-[#faf6f0] p-4 text-[#2d221b] space-y-1">
                <p className="font-semibold">Au creux de maman – Keraudran Maïwenn</p>
<p>19 rue Louis Le Moenic — 56240 Inguiniel – France</p>
                <p>
                  E-mail :{" "}
                  <a href="mailto:aucreuxdemaman@gmail.com" className="text-[#b85c38] font-medium hover:underline">
                    aucreuxdemaman@gmail.com
                  </a>
                </p>
              </div>
              <p>Un formulaire type de rétractation est mis à disposition du client.</p>
            </div>
          </div>

          {/* Article 10 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 10 – Demande d&apos;exécution avant la fin du délai de rétractation
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Lorsque le client souhaite que la prestation commence avant la fin du délai légal de rétractation de 14 jours, il doit en faire la <strong>demande expresse</strong>.
              </p>
              <p>
                Lorsque la prestation a commencé à la demande expresse du client avant la fin du délai de rétractation, le client qui exerce ensuite son droit de rétractation reste redevable du montant correspondant au service effectivement réalisé jusqu&apos;à la communication de sa décision de se rétracter.
              </p>
              <p>
                Lorsque la prestation a été entièrement exécutée avant la fin du délai de rétractation, après demande expresse du client et reconnaissance par celui-ci de la perte de son droit de rétractation après exécution complète, le droit de rétractation ne peut plus être exercé.
              </p>
            </div>
          </div>

          {/* Article 11 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 11 – Conditions relatives aux prestations de bien-être
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Les prestations de massage, réflexologie, bain enveloppé et autres prestations de bien-être proposées par Au creux de maman ne constituent pas des actes médicaux.
              </p>
              <p>
                Elles ne permettent pas d&apos;établir un diagnostic médical et ne remplacent pas une consultation ou un traitement médical.
              </p>
              <p>
                Le client s&apos;engage à signaler, avant la prestation, toute situation particulière pouvant nécessiter l&apos;avis préalable d&apos;un professionnel de santé.
              </p>
              <p>
                En cas de doute concernant son état de santé ou celui de son enfant, le client doit solliciter l&apos;avis d&apos;un professionnel de santé compétent.
              </p>
              <p>
                Au creux de maman se réserve le droit de reporter ou de refuser une prestation lorsqu&apos;elle estime que les conditions de sécurité nécessaires ne sont pas réunies.
              </p>
            </div>
          </div>

          {/* Article 12 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 12 – Accompagnement à l&apos;allaitement
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Les consultations d&apos;allaitement proposées ont pour objectif d&apos;apporter une écoute, des informations et un accompagnement pratique.
              </p>
              <p>
                Elles ne se substituent pas à un diagnostic ou à une prise en charge médicale.
              </p>
              <p>
                En cas de difficulté médicale, de douleur importante, de fièvre, de suspicion de complication ou de toute autre situation nécessitant une prise en charge médicale, le client est invité à consulter rapidement un professionnel de santé.
              </p>
            </div>
          </div>

          {/* Article 13 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 13 – Responsabilité du client
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Le client est responsable de l&apos;exactitude des informations communiquées lors de la réservation et avant la prestation.
              </p>
              <p>
                Il doit informer Au creux de maman de toute circonstance particulière pouvant avoir une incidence sur la réalisation de la prestation.
              </p>
            </div>
          </div>

          {/* Article 14 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 14 – Force majeure
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Aucune des parties ne pourra être tenue responsable de l&apos;inexécution ou du retard dans l&apos;exécution d&apos;une obligation lorsque celui-ci résulte d&apos;un événement de force majeure au sens du droit français.
              </p>
            </div>
          </div>

          {/* Article 15 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 15 – Données personnelles
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Dans le cadre de son activité, Au creux de maman peut être amenée à collecter et traiter des données personnelles nécessaires notamment à la prise de rendez-vous, à la gestion des prestations, à la facturation et à la communication avec le client.
              </p>
              <p>
                Les informations relatives au traitement des données personnelles sont détaillées dans la <strong>Politique de confidentialité</strong> accessible sur le site.
              </p>
              <p>
                Lorsque la réservation est effectuée par Calendly, certaines données sont traitées par Calendly pour le compte d&apos;Au creux de maman dans le cadre de la prise de rendez-vous.
              </p>
            </div>
          </div>

          {/* Article 16 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 16 – Réclamations
            </h2>
            <div className="mt-4 space-y-3">
              <p>Toute réclamation doit être adressée en priorité à :</p>
              <div className="rounded-2xl border border-[#ebdcd0] bg-[#faf6f0] p-4 text-[#2d221b] space-y-1">
                <p className="font-semibold">Au creux de maman – Keraudran Maïwenn</p>
<p>19 rue Louis Le Moenic — 56240 Inguiniel</p>
                <p>
                  E-mail :{" "}
                  <a href="mailto:aucreuxdemaman@gmail.com" className="text-[#b85c38] font-medium hover:underline">
                    aucreuxdemaman@gmail.com
                  </a>
                </p>
              </div>
              <p>Au creux de maman s&apos;efforcera de traiter la réclamation dans les meilleurs délais.</p>
            </div>
          </div>

          {/* Article 17 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 17 – Médiation de la consommation
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                Conformément aux dispositions du Code de la consommation relatives à la médiation de la consommation, le consommateur peut, après avoir préalablement adressé une réclamation écrite au professionnel et en l&apos;absence de réponse satisfaisante ou de réponse dans le délai applicable, recourir gratuitement au médiateur de la consommation dont relève le professionnel.
              </p>
              <p>Le médiateur désigné est :</p>
              <div className="rounded-2xl border border-[#ebdcd0] bg-[#faf6f0] p-4 text-[#2d221b] space-y-1">
                <p className="font-semibold">Société Médiation Professionnelle – Médiation de la consommation</p>
                <p>24 rue Albert de Mun — 33000 Bordeaux — France</p>
                <p>
                  E-mail :{" "}
                  <a href="mailto:saisine@mediateur-consommation-smp.fr" className="text-[#b85c38] font-medium hover:underline">
                    saisine@mediateur-consommation-smp.fr
                  </a>
                </p>
                <p>
                  Site :{" "}
                  <a href="https://www.mediateur-consommation-smp.fr/" target="_blank" rel="noreferrer" className="text-[#b85c38] font-medium hover:underline">
                    https://www.mediateur-consommation-smp.fr/
                  </a>
                </p>
              </div>
              <p>La saisine peut notamment être effectuée selon les modalités indiquées sur le site du médiateur.</p>
            </div>
          </div>

          {/* Article 18 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 18 – Droit applicable
            </h2>
            <div className="mt-4 space-y-3">
              <p>Les présentes CGV sont soumises au droit français.</p>
              <p>
                En cas de litige, le consommateur peut exercer les droits qui lui sont accordés par les dispositions impératives du Code de la consommation.
              </p>
            </div>
          </div>

          {/* Article 19 */}
          <div>
            <h2 className="font-display text-2xl font-medium text-[#2d221b] border-b border-[#ebdcd0] pb-2">
              Article 19 – Acceptation des CGV
            </h2>
            <div className="mt-4 space-y-3">
              <p>
                La réservation d&apos;une prestation implique la prise de connaissance et l&apos;acceptation des présentes CGV, dans les conditions prévues par la réglementation applicable.
              </p>
              <p>
                Les présentes CGV sont accessibles à tout moment sur le site internet d&apos;Au creux de maman.
              </p>
            </div>
          </div>

        </div>
      </section>
    </div>
  )
}

export default CGV
